import type { Session, KnittingEvent } from '../types/session';

export interface LifecycleEvent {
  sessionId: string;
  patternId: string;
}

export interface SessionDayEntry {
  patternId: string;
  ms: number;
}

export interface DayActivity {
  dayKey: string; // "YYYY-MM-DD" in device local TZ
  totalMs: number;
  /** sessionId → time spent on that session that day. */
  perSession: Map<string, SessionDayEntry>;
  /** Derived: patternId → time spent on that pattern (summed across sessions). */
  perPattern: Map<string, number>;
  starts: LifecycleEvent[];
  finishes: LifecycleEvent[];
  dominantPatternId: string | null;
}

export type ActivityMap = Map<string, DayActivity>;

/** Format a Date as "YYYY-MM-DD" using device-local fields. */
export function dayKeyOf(d: Date): string {
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Returns the local-midnight Date for the day after `d`. */
function nextMidnightLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
}

function ensureDay(map: ActivityMap, key: string): DayActivity {
  let day = map.get(key);
  if (!day) {
    day = {
      dayKey: key,
      totalMs: 0,
      perSession: new Map(),
      perPattern: new Map(),
      starts: [],
      finishes: [],
      dominantPatternId: null,
    };
    map.set(key, day);
  }
  return day;
}

/**
 * Per-local-day active ms for a SINGLE session's history. Splits intervals at
 * local midnight; excludes PAUSE windows; caps a dangling open interval at the
 * last event. Returned sorted by dayKey. The same shape is persisted as
 * `Session.activityDays` at archive time so live and archived attribution share
 * one implementation.
 */
export function sessionActivityDays(history: KnittingEvent[]): [string, number][] {
  const perDay = new Map<string, number>();
  const add = (start: Date, end: Date) => {
    let cursor = start;
    while (cursor < end) {
      const dayK = dayKeyOf(cursor);
      const dayEnd = nextMidnightLocal(cursor);
      const sliceEnd = end < dayEnd ? end : dayEnd;
      const ms = sliceEnd.getTime() - cursor.getTime();
      if (ms > 0) perDay.set(dayK, (perDay.get(dayK) ?? 0) + ms);
      cursor = sliceEnd;
    }
  };

  let activeSince: Date | null = null;
  for (const ev of history) {
    const ts = new Date(ev.timestamp);
    if (RESUME_ACTIONS.has(ev.action)) {
      if (activeSince === null) activeSince = ts;
    } else if (PAUSE_ACTIONS.has(ev.action)) {
      if (activeSince !== null) {
        add(activeSince, ts);
        activeSince = null;
      }
    }
  }
  if (activeSince !== null && history.length > 0) {
    const lastTs = new Date(history[history.length - 1].timestamp);
    if (lastTs > activeSince) add(activeSince, lastTs);
  }

  return Array.from(perDay.entries()).sort((a, b) =>
    a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0,
  );
}

/** Attribute a session's per-day ms list onto the activity map. */
function attributeDays(
  map: ActivityMap,
  sessionId: string,
  patternId: string,
  days: [string, number][],
): void {
  for (const [dayK, ms] of days) {
    if (ms <= 0) continue;
    const day = ensureDay(map, dayK);
    day.totalMs += ms;
    const existing = day.perSession.get(sessionId);
    if (existing) existing.ms += ms;
    else day.perSession.set(sessionId, { patternId, ms });
    day.perPattern.set(patternId, (day.perPattern.get(patternId) ?? 0) + ms);
  }
}

function finalizeDominant(map: ActivityMap): void {
  for (const day of map.values()) {
    let bestId: string | null = null;
    let bestMs = -1;
    // Sort patternIds for deterministic tie-breaking.
    const sortedIds = Array.from(day.perPattern.keys()).sort();
    for (const id of sortedIds) {
      const ms = day.perPattern.get(id)!;
      if (ms > bestMs) {
        bestMs = ms;
        bestId = id;
      }
    }
    day.dominantPatternId = bestId;
  }
}

const RESUME_ACTIONS = new Set<KnittingEvent['action']>(['START', 'RESUME']);
const PAUSE_ACTIONS = new Set<KnittingEvent['action']>(['PAUSE']);

// `_now` is reserved for a future "future-event guard" (don't count events past
// now). The v1 spec accepted leaving this unused and capping dangling chunks at
// the last event timestamp instead.
export function aggregateActivity(sessions: Session[], _now: Date): ActivityMap {
  const map: ActivityMap = new Map();

  for (const s of sessions) {
    if (s.history.length > 0) {
      // Live session: markers from history, time from the shared chunker.
      const firstKey = dayKeyOf(new Date(s.history[0].timestamp));
      ensureDay(map, firstKey).starts.push({ sessionId: s.id, patternId: s.patternId });

      if (s.status === 'completed' || s.status === 'archived') {
        const lastKey = dayKeyOf(new Date(s.history[s.history.length - 1].timestamp));
        ensureDay(map, lastKey).finishes.push({ sessionId: s.id, patternId: s.patternId });
      }

      attributeDays(map, s.id, s.patternId, sessionActivityDays(s.history));
    } else if (s.status === 'archived') {
      // Archived session: history was discarded. Markers come from the
      // start/finish timestamps; per-day time from the breadcrumbs captured at
      // archive time (`activityDays`). Older archives without `activityDays`
      // get markers only — still better than vanishing from the calendar.
      ensureDay(map, dayKeyOf(new Date(s.startedAt))).starts.push({
        sessionId: s.id,
        patternId: s.patternId,
      });
      ensureDay(map, dayKeyOf(new Date(s.updatedAt))).finishes.push({
        sessionId: s.id,
        patternId: s.patternId,
      });
      if (s.activityDays) {
        attributeDays(map, s.id, s.patternId, s.activityDays);
      }
    }
    // else: idle/never-knit session with no history — nothing to record.
  }

  finalizeDominant(map);
  return map;
}

export interface StreakInfo {
  currentDays: number;
  todayActive: boolean;
}

function shiftDayKey(key: string, deltaDays: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y, m - 1, d + deltaDays);
  return dayKeyOf(dt);
}

export function computeStreak(activity: ActivityMap, now: Date): StreakInfo {
  const todayKey = dayKeyOf(now);
  const yesterdayKey = shiftDayKey(todayKey, -1);

  const todayActive = (activity.get(todayKey)?.totalMs ?? 0) > 0;

  let anchor: string | null = null;
  if (todayActive) anchor = todayKey;
  else if ((activity.get(yesterdayKey)?.totalMs ?? 0) > 0) anchor = yesterdayKey;

  if (anchor === null) return { currentDays: 0, todayActive: false };

  let count = 0;
  let cursor = anchor;
  while ((activity.get(cursor)?.totalMs ?? 0) > 0) {
    count++;
    cursor = shiftDayKey(cursor, -1);
  }

  return { currentDays: count, todayActive };
}

export function monthRange(activity: ActivityMap): {
  earliest: string | null;
  latest: string | null;
} {
  if (activity.size === 0) return { earliest: null, latest: null };
  let earliest = '\uFFFF';
  let latest = '';
  for (const key of activity.keys()) {
    if (key < earliest) earliest = key;
    if (key > latest) latest = key;
  }
  return { earliest, latest };
}
